import { X } from 'lucide-react'
import { ReactNode, useEffect } from 'react'

import './Drawer.css'

interface DrawerProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  actions?: ReactNode
  width?: number
  /**
   * When true: drawer floats over the right side as a peek panel — no
   * backdrop, no body-scroll lock, no auto-close on outside click. Page
   * remains fully interactive. ESC still closes. Use this for "always-on"
   * preview side panels (deal page document peek).
   */
  nonBlocking?: boolean
  children?: ReactNode
}

/**
 * Right-side slide-in panel used to preview a record without leaving the
 * current page.
 *
 * Default (modal): closes on ESC, backdrop click, or X. Locks body scroll.
 * `nonBlocking`: floats without backdrop; closes only on ESC or X.
 */
export function Drawer({
  open,
  onClose,
  title,
  actions,
  width = 720,
  nonBlocking = false,
  children,
}: DrawerProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open || nonBlocking) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open, nonBlocking])

  if (!open) return null

  const panel = (
    <div
      className={`drawer ${nonBlocking ? 'drawer-floating' : ''}`}
      style={{ width }}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal={!nonBlocking}
    >
      <div className="drawer-header">
        <div className="drawer-header-left">{title}</div>
        <div className="drawer-header-right">
          {actions}
          <button
            className="drawer-close"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      <div className="drawer-body">{children}</div>
    </div>
  )

  if (nonBlocking) return panel

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      {panel}
    </div>
  )
}
